import { CSSProperties, FunctionComponent, useEffect } from "react";
import './styles/DragPreview.scss';

import { useDragLayer } from "react-dnd";

import { componentClassName } from "@dw/utils";

import { DragItem } from "./types";
import { IDragDropZone, useDragAndDropContext } from "./DragAndDropProvider";

const getClosestTick = (ticks: number[], currentPos: number): number => {
  return ticks.reduce((closest, current) =>
    Math.abs(current - currentPos) < Math.abs(closest - currentPos) ? current : closest
  );
}

interface GetItemStylesOptions {
  x?: number;
  y?: number;
  activeDropZone?: IDragDropZone;
  verticalTicksPerDropZone?: number;
  horizontalTicksPerDropZone?: number;
}
function calculateItemPosition({ x, y, activeDropZone, verticalTicksPerDropZone, horizontalTicksPerDropZone }: GetItemStylesOptions): { styles: CSSProperties; verticalTickIndex?: number; horizontalTickIndex?: number; } {
  if (typeof x !== 'number' || typeof y !== 'number') {
    return {
      styles: {
        display: 'none',
      }
    }
  }

  let xToUse = x;
  let yToUse = y;
  let verticalTickIndex: number | undefined = undefined;
  let horizontalTickIndex: number | undefined = undefined;
  let dropZonePos: DOMRect | undefined = undefined;

  if (verticalTicksPerDropZone && activeDropZone?.element && !activeDropZone?.noVerticalTicks) {
    dropZonePos = activeDropZone.element.getBoundingClientRect();

    const snappingPoints: number[] = [];
    const interval = dropZonePos.height / verticalTicksPerDropZone;
    for (let i = dropZonePos.y; i < dropZonePos.y + dropZonePos.height; i += interval) {
      snappingPoints.push(i);
    }

    yToUse = getClosestTick(snappingPoints, yToUse);
    verticalTickIndex = snappingPoints.indexOf(yToUse);
  }

  if (horizontalTicksPerDropZone && activeDropZone?.element && !activeDropZone?.noHorizontalTicks) {
    dropZonePos = dropZonePos ?? activeDropZone.element.getBoundingClientRect();

    const snappingPoints: number[] = [];
    const interval = dropZonePos.width / horizontalTicksPerDropZone;
    for (let i = dropZonePos.x; i < dropZonePos.x + dropZonePos.width; i += interval) {
      snappingPoints.push(i);
    }

    xToUse = getClosestTick(snappingPoints, xToUse);
    horizontalTickIndex = snappingPoints.indexOf(xToUse);
  } else if (dropZonePos) {
    xToUse = dropZonePos.x;
  }

  const transform = `translate(${xToUse}px, ${yToUse}px)`
  return {
    styles: {
      transform,
      WebkitTransform: transform,
    },
    verticalTickIndex,
    horizontalTickIndex
  }
}

export const DragPreview: FunctionComponent = () => {

  const { activeDropZone, verticalTicksPerDropZone, horizontalTicksPerDropZone, setActiveDropZoneCurrentDirectionalIndexes } = useDragAndDropContext();

  const { isDragging, item, offset } = useDragLayer(
    (monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem() as DragItem<unknown> | null,
      offset: {
        x: monitor.getClientOffset()?.x,
        y: monitor.getClientOffset()?.y
      }
    })
  );

  const { styles, verticalTickIndex, horizontalTickIndex } = calculateItemPosition({
    ...offset,
    activeDropZone,
    verticalTicksPerDropZone,
    horizontalTicksPerDropZone
  });

  useEffect(() => {
    if (!activeDropZone) return;

    // If you want "no tick" to clear, keep undefined; otherwise guard it:
    // if (typeof verticalTickIndex !== "number") return;

    if (activeDropZone.currentVerticalIndex !== verticalTickIndex || activeDropZone.currentHorizontalIndex !== horizontalTickIndex) {
      setActiveDropZoneCurrentDirectionalIndexes(verticalTickIndex, horizontalTickIndex);
    }
  }, [
    activeDropZone,
    activeDropZone?.currentVerticalIndex,
    verticalTickIndex,
    activeDropZone?.currentHorizontalIndex,
    horizontalTickIndex,
    setActiveDropZoneCurrentDirectionalIndexes,
  ]);

  if (!isDragging || !item) return null;

  const cn = componentClassName('drag-preview');

  return (
    <div className={cn()}>
      <div style={styles}>
        {/* The preview node came from the draggable */}
        {item.preview}
      </div>
    </div>
  );
};
